import { useContext, useEffect, useState } from "react";
import { Input, Form, Select, DatePicker, Space, Button } from "antd";
import dayjs from "dayjs";

import { AuthContext } from "../../context/AuthContext";
import { useFormContext } from "../../hooks/useFormContext";
import { toast } from "../../utils/toast";
import { calculateAgeFromBirthDate } from "../../utils/ageHelper";

import {
  getCompanyProductsApi,
  getProductApi,
} from "../../services/Product/ProductApis";

import type { SelectProps } from "antd";

interface StepProps {
  gotoStep: Function;
  current: number;
  form: any;
  handleAge: Function;
  labelCol?: object;
  wrapperCol?: object;
}

const coveredOpt: SelectProps["options"] = [
  {
    label: "Extended",
    value: "Extended",
  },
  {
    label: "Dependant",
    value: "Dependant",
  },
  {
    label: "Spouse",
    value: "Spouse",
  },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 8 },
};

const CoveredLives: React.FC<StepProps> = ({
  gotoStep,
  current,
  form,
  handleAge,
  labelCol,
  wrapperCol,
}) => {
  const { coveredLive, groupInfo, productInfo, setCoveredProductInfo } =
    useFormContext();
  const { currentGroup } = useContext(AuthContext);
  const [isExtended, setIsExtended] = useState(false);
  const [coveredAge, setCoveredAge] = useState(0);
  const [groupProducts, setGroupProducts] = useState<any>();
  const [products, setProducts] = useState<SelectProps["options"]>();
  const [fetchLoading, setFetchLoading] = useState(false);

  // Check if Product Type is anthing other than one plus add additional input below dob
  const onChangeType = (e: string) => {
    setIsExtended(
      e === "Extended" && productInfo?.PlanCategory?.Name !== "One Plus"
    );
  };

  const extractDateOfBirth = (idNumber: string) => {
    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);

    // Adjust the year for the 1900s or 2000s
    const currentYear = new Date().getFullYear();
    const birthYear = year + (year < currentYear % 100 ? 2000 : 1900);

    return new Date(birthYear, month - 1, day); // Month is zero-based
  };

  const onChangeId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id: string = e.target.value;

    const age = handleDateOfBirth(id);

    if (groupProducts && groupProducts.length > 0 && !Number.isNaN(age)) {
      // First filter based on age
      const filteredProductsAge = groupProducts.filter(
        (product: any) =>
          product.ProductCategory &&
          product.ProductCategory.MinAge <= age &&
          age <= product.ProductCategory.MaxAge
      );

      // Then filter based on PlanCategory.id === 3
      const filteredProductCategory = filteredProductsAge.filter(
        (product: any) => product.PlanCategory.id === 3
      );

      // Set products directly with the filtered products
      setProducts(
        filteredProductCategory.map((product: any) => ({
          value: product.id,
          label: product.ProductName,
        }))
      );
    }
  };

  const onChangeProduct = async (e: string) => {
    const { data } = await getProductApi(e);
    setCoveredProductInfo(data.id);
  };

  const handleDateOfBirth = (idNumber: string) => {
    const dateOfBirth = extractDateOfBirth(idNumber);

    form.setFieldsValue({
      CoveredDateOfBirth: dayjs(dateOfBirth),
    });

    const roundedAge = calculateAgeFromBirthDate(dateOfBirth);
    setCoveredAge(roundedAge);

    return roundedAge;
  };

  useEffect(() => {
    getCompanyProductsApi(currentGroup)
      .then((res) => {
        if (!res.data || res.data.length === 0) {
          toast.warning({
            title: 'No Products Available',
            description: 'No products are configured for this group. Extended coverage options may be limited.',
            duration: 6,
          });
          setGroupProducts([]);
          return;
        }
        setGroupProducts(res.data);
      })
      .catch((err) => {
        console.error('Error fetching products:', err);
        toast.error({
          title: 'Error Loading Products',
          description: 'Failed to load products for covered lives.',
        });
      });

    form.setFieldsValue({
      ...coveredLive,
      CoveredDateOfBirth:
        coveredLive.CoveredDateOfBirth &&
        dayjs(coveredLive.CoveredDateOfBirth, "YYYY-MM-DD"),
    });

    const formData = form.getFieldsValue();
    setIsExtended(
      formData.CoveredType === "Extended" &&
        productInfo.PlanCategory.Name !== "One Plus"
    );
  }, [coveredLive]);

  const itemLayout = {
    labelCol: { style: { width: '155px', flexShrink: 0 } },
    wrapperCol: { style: { flex: 1 } },
  };

  const tailButtonLayout = {
    wrapperCol: { style: { marginLeft: '155px' } },
  };

  const content = (
    <>
      <Form.Item name="CoveredType" label="Covered Type" {...itemLayout}>
        <Select
          onChange={(e: any) => onChangeType(e)}
          options={coveredOpt}
          size="large"
          style={{ width: "100%" }}
          placeholder="Select Covered Type"
        />
      </Form.Item>
      <Form.Item name="CoveredName" label="Covered Name" {...itemLayout}>
        <Input size="large" placeholder="Name" />
      </Form.Item>
      <Form.Item name="CoveredSurname" label="Covered Surname" {...itemLayout}>
        <Input size="large" placeholder="Surname" />
      </Form.Item>
      <Form.Item name="CoveredIdNumber" label="Covered ID Number" {...itemLayout}>
        <Input
          size="large"
          placeholder="Covered ID Number"
          onChange={(e) => onChangeId(e)}
          maxLength={13}
        />
      </Form.Item>

      <Form.Item name="CoveredDateOfBirth" label="Covered Date Of Birth" {...itemLayout}>
        <DatePicker
          placeholder="Covered Date Of Birth"
          size="large"
          style={{ width: "100%" }}
        />
      </Form.Item>

      {isExtended && (
        <Form.Item name="ExtendedProduct" label="Extended Product" {...itemLayout}>
          <Select
            loading={fetchLoading}
            onChange={(e) => onChangeProduct(e)}
            options={products}
            size="large"
            style={{ width: "100%" }}
            placeholder="Select Product"
          />
        </Form.Item>
      )}

      <Form.Item {...tailButtonLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Next button rendered by parent modal */}
        </Space>
      </Form.Item>
    </>
  );
  return content;
};
export default CoveredLives;
